import { Match, FIRST_SEASON_ID } from '../types';
import { storage } from './storage';

// Maç istatistiklerini oyunculara uygula
export const applyMatchStats = async (match: Match) => {
  // Tam liste kaydı öncesi taze liste — eski önbellekteki silinen oyuncuyu geri yazmayı önler
  const allPlayers = await storage.getPlayers({ forceRefresh: true });
  const seasonId = match.seasonId;
  
  // Match verilerinin geçerli olduğundan emin ol
  if (!match || !match.blackTeam || !match.whiteTeam) {
    return;
  }
  
  const updatedPlayers = allPlayers.map(player => {
    const isInBlackTeam = match.blackTeam.includes(player.id);
    const isInWhiteTeam = match.whiteTeam.includes(player.id);
    const played = isInBlackTeam || isInWhiteTeam;

    if (!played) return player;

    // player.stats undefined olabilir, kontrol et
    if (!player.stats) {
      player.stats = {};
    }

    const seasonStats = player.stats[seasonId] || {
      matches: 0, wins: 0, draws: 0, losses: 0,
      goals: 0, assists: 0, ownGoals: 0, points: 0,
      blackCards: 0, mvdpCards: 0,
    };

    const blackWon = match.blackScore > match.whiteScore;
    const whiteWon = match.whiteScore > match.blackScore;
    const draw = match.blackScore === match.whiteScore;

    const newStats = { ...seasonStats };
    newStats.matches += 1;

    if (isInBlackTeam) {
      if (blackWon) newStats.wins += 1;
      else if (draw) newStats.draws += 1;
      else newStats.losses += 1;
    } else {
      if (whiteWon) newStats.wins += 1;
      else if (draw) newStats.draws += 1;
      else newStats.losses += 1;
    }

    if (match.events && Array.isArray(match.events)) {
      match.events.forEach(event => {
        if (event.scorerId === player.id && !event.ownGoal) {
          newStats.goals += 1;
        }
        if (event.scorerId === player.id && event.ownGoal) {
          newStats.ownGoals += 1;
        }
        if (event.assistId === player.id) {
          newStats.assists += 1;
        }
      });
    }

    if (match.blackCard === player.id) newStats.blackCards += 1;
    if (match.mvdp === player.id) newStats.mvdpCards += 1;

    // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
    if (seasonId === FIRST_SEASON_ID) {
      newStats.points = newStats.wins - newStats.losses;
    } else {
      newStats.points = newStats.wins * 3 + newStats.draws * 1;
    }

    return {
      ...player,
      stats: {
        ...player.stats,
        [seasonId]: newStats,
      },
    };
  });

  await storage.savePlayers(updatedPlayers);
};

// Maç istatistiklerini oyunculardan geri al (maç silme için)
export const removeMatchStats = async (match: Match) => {
  const allPlayers = await storage.getPlayers({ forceRefresh: true });
  const seasonId = match.seasonId;
  
  // Match verilerinin geçerli olduğundan emin ol
  if (!match || !match.blackTeam || !match.whiteTeam) {
    return;
  }
  
  const updatedPlayers = allPlayers.map(player => {
    const isInBlackTeam = match.blackTeam.includes(player.id);
    const isInWhiteTeam = match.whiteTeam.includes(player.id);
    const played = isInBlackTeam || isInWhiteTeam;

    if (!played) return player;

    // player.stats undefined olabilir, kontrol et
    if (!player.stats) {
      player.stats = {};
    }

    const seasonStats = player.stats[seasonId];
    if (!seasonStats) return player;

    const blackWon = match.blackScore > match.whiteScore;
    const whiteWon = match.whiteScore > match.blackScore;
    const draw = match.blackScore === match.whiteScore;

    const newStats = { ...seasonStats };
    newStats.matches = Math.max(0, newStats.matches - 1);

    if (isInBlackTeam) {
      if (blackWon) newStats.wins = Math.max(0, newStats.wins - 1);
      else if (draw) newStats.draws = Math.max(0, newStats.draws - 1);
      else newStats.losses = Math.max(0, newStats.losses - 1);
    } else {
      if (whiteWon) newStats.wins = Math.max(0, newStats.wins - 1);
      else if (draw) newStats.draws = Math.max(0, newStats.draws - 1);
      else newStats.losses = Math.max(0, newStats.losses - 1);
    }

    match.events.forEach(event => {
      if (event.scorerId === player.id && !event.ownGoal) {
        newStats.goals = Math.max(0, newStats.goals - 1);
      }
      if (event.scorerId === player.id && event.ownGoal) {
        newStats.ownGoals = Math.max(0, newStats.ownGoals - 1);
      }
      if (event.assistId === player.id) {
        newStats.assists = Math.max(0, newStats.assists - 1);
      }
    });

    if (match.blackCard === player.id) {
      newStats.blackCards = Math.max(0, newStats.blackCards - 1);
    }
    if (match.mvdp === player.id) {
      newStats.mvdpCards = Math.max(0, newStats.mvdpCards - 1);
    }

    // Puan: 1. sezon = G-M; 2. sezon+ = Galibiyet 3, Beraberlik 1, Mağlubiyet 0
    if (seasonId === FIRST_SEASON_ID) {
      newStats.points = newStats.wins - newStats.losses;
    } else {
      newStats.points = newStats.wins * 3 + newStats.draws * 1;
    }

    return {
      ...player,
      stats: {
        ...player.stats,
        [seasonId]: newStats,
      },
    };
  });

  await storage.savePlayers(updatedPlayers);
};

// Maç istatistiklerini güncelle (eski maçı geri al, yeni maçı uygula)
export const updateMatchStats = async (oldMatch: Match, newMatch: Match) => {
  await removeMatchStats(oldMatch);
  await applyMatchStats(newMatch);
};
